import React, { useEffect } from 'react';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { AnalyticsEvent, logEvent, EventParams, ClarityTags } from '@weddingspot/ws-analytics';
import { VenueProfileProps } from 'components/venueProfile/types';
import { VenueProfileContextProvider } from 'components/venueProfile/context';
import VenueProfile from 'components/venueProfile';
import { VendorsApi, Venues } from '@weddingspot/ws-api-client';
import { LocalStorage, StorableDataKeys, HeaderContext } from '@weddingspot/ws-core-components';
import { parseVenueIdFromQuery } from 'utils/uriExtractor';
import ErrorPage from '../_error';
import Head from 'next/head';

declare const clarity: any | undefined;

const VenueProfilePage = ({ notFound, conditionalVenueProfile, venueId }: VenueProfileProps) => {
    const { loadHeaderData } = React.useContext(HeaderContext);

    if (notFound || !conditionalVenueProfile) {
        return <ErrorPage statusCode={404} />;
    }

    if (conditionalVenueProfile.is_not_available) {
        return <ErrorPage statusCode={404} />;
    }

    useEffect(() => {
        /**
         * clarity is a script provided by MS Clarity to add custom tag. Helps filtering user's traffic and seesion recording 
         * on the basis of the tag.
         * For detail, Refer https://learn.microsoft.com/en-us/clarity/filters/custom-tags#how-to-setup-custom-tags
         * 
         * Currently, have use clarity script directly as of now. However, there is ClarityBackend.setCurUserProps function
         * that was supposed to run the script with key & value, accepted as prop object. But, it's not working for far too long. 
         */

        clarity("set", ClarityTags.VENUE_PROFILE_PAGE_VERSION, '3')
        logEvent(AnalyticsEvent.EVTNM_VISITED_WEDDING_VENUE_PROFILE, { version: '3', [EventParams.VENUE_ID]: Number(venueId) });
        loadHeaderData(conditionalVenueProfile.id);

        let visitedVenues = LocalStorage.getItem(StorableDataKeys.VISITED_VENUES);

        if (!visitedVenues) {
            LocalStorage.setItem(StorableDataKeys.VISITED_VENUES, []);
            visitedVenues = [];
        }

        if (!visitedVenues.includes(`${venueId}`)) {
            visitedVenues = [...visitedVenues, venueId];
            LocalStorage.setItem(StorableDataKeys.VISITED_VENUES, visitedVenues);
        }
    }, [conditionalVenueProfile.id, loadHeaderData, venueId]);

    return (
        <>
            <Head>
                <title>{conditionalVenueProfile.page_title || 'Wedding Venues &amp; Planning Tools | Wedding Spot'}</title>
                <meta name="description" content={conditionalVenueProfile.meta_description}/>
                <link rel="canonical" href={conditionalVenueProfile.canonical_url} />
                { (!!conditionalVenueProfile.schema_org) ?
                    (<script
                        type='application/ld+json'
                        dangerouslySetInnerHTML={{ __html: `${conditionalVenueProfile.schema_org}` }}
                    />) : null
                }
            </Head>
            <VenueProfileContextProvider
                venueId={Number(venueId)}
                preloadWsVenueProfile={conditionalVenueProfile as Venues.VenueDetails}
            >
                <VenueProfile />
            </VenueProfileContextProvider>
        </>

    );
};

export const getServerSideProps: GetServerSideProps<VenueProfileProps> = async (context: GetServerSidePropsContext) => {
    const venueId = parseVenueIdFromQuery(context.query);

    if (Number.isNaN(venueId)) {
        return { notFound: true };
    }

    try {
        const conditionalVenueProfile = await VendorsApi.venueDetails(venueId);

        if (conditionalVenueProfile.force_v2) {
            // need base url here just to make it work, we're using local host anyway
            const url = new URL(context.req.url || '', 'https://www.wedding-spot.com');
            url.searchParams.set('forceversion', 'v2');
            return {
                redirect: {
                    destination: url.pathname + url.search,
                    permanent: false,
                },
            };
        }

        return {
            props: {
                conditionalVenueProfile,
                notFound: false,
                venueId,
            },
        };
    } catch (err) {
        return { notFound: true };
    }
};

export default VenueProfilePage;
